import { useReducer, useCallback } from 'react';
import {
  CronosEthereumTransferType,
  ExternalBridgeType,
  Network,
  TokenAsset,
} from '@types';
import { getDefaultNetworks, restrictInput, sanitizeInput } from '@lib/utils';
import useValidateTransferAssetFlow, {
  ValidateParams,
  ValidateResult,
} from '@hooks/useValidateTransferAssetFlow';
import staticConfig from '@config/static-config';
import { UseBridgeTransactionFeeInfo } from '@hooks/useBridgeTransactionFee';

const { networks } = staticConfig;

const networksConfig =
  process.env.NEXT_PUBLIC_DEFAULT_NETWORK_ENV === 'mainnet'
    ? networks.mainnet
    : networks.testnet;

const defaultNetworks = getDefaultNetworks(networksConfig);

export enum TransferAssetFlowStep {
  ProvideDetails = 1,
  ConfirmDetails = 2,
  BridgeTransaction = 3,
}

export interface TransferAssetFlowReducerState {
  amount: string;
  cronosEthereumTransferType: CronosEthereumTransferType | null;
  defaultFromNetwork: Network;
  defaultToNetwork: Network;
  feeInfo: UseBridgeTransactionFeeInfo | null;
  fromNetwork: Network;
  hasSwitchedNetwork: boolean;
  showAssetSelection: boolean;
  showWalletConnection: boolean;
  externalBridgeType: ExternalBridgeType | null;
  step: TransferAssetFlowStep;
  toAddress: string;
  toNetwork: Network;
  tokenAsset: TokenAsset | null;
  tokenBalance: string;
  validationErrors: ValidateResult['errors'];
}

export const initState: TransferAssetFlowReducerState = {
  amount: '0',
  cronosEthereumTransferType: null,
  defaultFromNetwork: defaultNetworks.fromNetwork,
  defaultToNetwork: defaultNetworks.toNetwork,
  feeInfo: null,
  fromNetwork: defaultNetworks.fromNetwork,
  hasSwitchedNetwork: false,
  showAssetSelection: false,
  showWalletConnection: false,
  externalBridgeType: null,
  step: TransferAssetFlowStep.ProvideDetails,
  toAddress: '',
  toNetwork: defaultNetworks.toNetwork,
  tokenAsset: null,
  tokenBalance: '',
  validationErrors: {},
};

const ON_TO_ADDRESS_CHANGE = 'ON_ADDRESS_CHANGE';
const ON_AMOUNT_CHANGE = 'ON_AMOUNT_CHANGE';
const ON_CONFIRM_FEE_INFO = 'ON_CONFIRM_FEE_INFO';
const ON_CRONOS_ETHEREUM_TRANSFER_TYPE_CHANGE =
  'ON_CRONOS_ETHEREUM_TRANSFER_TYPE_CHANGE';
const ON_DEFAULT_NETWORKS_CHANGE = 'ON_DEFAULT_NETWORKS_CHANGE';
const ON_FLIP_NETWORKS = 'ON_FLIP_NETWORKS';
const ON_FROM_NETWORK_CHANGE = 'ON_FROM_NETWORK_CHANGE';
const ON_HIDE_ASSET_SELECTION = 'ON_HIDE_ASSET_SELECTION';
const ON_HIDE_WALLET_CONNECTION = 'ON_HIDE_WALLET_CONNECTION';
const ON_HIDE_EXTERNAL_BRIDGE_LINK = 'ON_HIDE_EXTERNAL_BRIDGE_LINK';
const ON_INITIAL_FROM_NETWORK_CHANGE = 'ON_INITIAL_FROM_NETWORK_CHANGE';
const ON_INITIAL_TO_NETWORK_CHANGE = 'ON_INITIAL_TO_NETWORK_CHANGE';
const ON_NEXT_STEP = 'ON_NEXT_STEP';
const ON_PREV_STEP = 'ON_PREV_STEP';
const ON_RESET = 'ON_RESET';
const ON_SHOW_ASSET_SELECTION = 'ON_SHOW_ASSET_SELECTION';
const ON_SHOW_WALLET_CONNECTION = 'ON_SHOW_WALLET_CONNECTION';
const ON_SHOW_EXTERNAL_BRIDGE_LINK = 'ON_SHOW_EXTERNAL_BRIDGE_LINK';
const ON_TOKEN_ASSET_CHANGE = 'ON_TOKEN_ASSET_CHANGE';
const ON_TOKEN_BALANCE_CHANGE = 'ON_TOKEN_BALANCE_CHANGE';
const ON_TO_NETWORK_CHANGE = 'ON_TO_NETWORK_CHANGE';
const ON_UPDATE_VALIDATION_ERRORS = 'ON_UPDATE_VALIDATION_ERRORS';

interface OnToAddressChangeAction {
  type: typeof ON_TO_ADDRESS_CHANGE;
  value: string;
}

interface OnAmountChangeAction {
  clearAmountValidationErrors: boolean;
  type: typeof ON_AMOUNT_CHANGE;
  value: string;
}

interface OnCronosEthereumTransferTypeChangeAction {
  type: typeof ON_CRONOS_ETHEREUM_TRANSFER_TYPE_CHANGE;
  value: CronosEthereumTransferType;
}

interface OnConfirmFeeInfoAction {
  type: typeof ON_CONFIRM_FEE_INFO;
  value: UseBridgeTransactionFeeInfo;
}

interface OnDefaultNetworksChangeActionValue {
  fromNetwork: Network;
  toNetwork: Network;
}

interface OnDefaultNetworksChangeAction {
  type: typeof ON_DEFAULT_NETWORKS_CHANGE;
  value: OnDefaultNetworksChangeActionValue;
}

interface OnFlipNetworkAction {
  type: typeof ON_FLIP_NETWORKS;
}

interface OnFromNetworkChangeAction {
  type: typeof ON_FROM_NETWORK_CHANGE;
  value: Network;
}

interface OnHideAssetSelectionAction {
  type: typeof ON_HIDE_ASSET_SELECTION;
}

interface OnHideWalletConnectionAction {
  type: typeof ON_HIDE_WALLET_CONNECTION;
}

interface OnHideExternalBridgeLinkAction {
  type: typeof ON_HIDE_EXTERNAL_BRIDGE_LINK;
}

interface OnInitialFromNetworkChangeAction {
  type: typeof ON_INITIAL_FROM_NETWORK_CHANGE;
  value: Network;
}

interface OnInitialToNetworkChangeAction {
  type: typeof ON_INITIAL_TO_NETWORK_CHANGE;
  value: Network;
}

interface OnNextStepAction {
  type: typeof ON_NEXT_STEP;
}
interface OnPrevStepAction {
  type: typeof ON_PREV_STEP;
}

interface OnResetAction {
  type: typeof ON_RESET;
}

interface OnShowAssetSelectionAction {
  type: typeof ON_SHOW_ASSET_SELECTION;
}

interface OnShowWalletConnectionAction {
  type: typeof ON_SHOW_WALLET_CONNECTION;
}

interface OnShowExternalBridgeLinkAction {
  type: typeof ON_SHOW_EXTERNAL_BRIDGE_LINK;
  value: ExternalBridgeType;
}

interface OnTokenAssetChangeAction {
  type: typeof ON_TOKEN_ASSET_CHANGE;
  value: TokenAsset;
}

interface OnTokenBalanceChangeAction {
  type: typeof ON_TOKEN_BALANCE_CHANGE;
  value: string;
}

interface OnToNetworkChangeAction {
  type: typeof ON_TO_NETWORK_CHANGE;
  value: Network;
}

interface OnUpdateValidationErrorsAction {
  type: typeof ON_UPDATE_VALIDATION_ERRORS;
  value: ValidateResult['errors'];
}

type TransferAssetFlowReducerAction =
  | OnAmountChangeAction
  | OnConfirmFeeInfoAction
  | OnCronosEthereumTransferTypeChangeAction
  | OnDefaultNetworksChangeAction
  | OnFlipNetworkAction
  | OnFromNetworkChangeAction
  | OnHideAssetSelectionAction
  | OnHideWalletConnectionAction
  | OnHideExternalBridgeLinkAction
  | OnInitialFromNetworkChangeAction
  | OnInitialToNetworkChangeAction
  | OnNextStepAction
  | OnPrevStepAction
  | OnResetAction
  | OnShowAssetSelectionAction
  | OnShowWalletConnectionAction
  | OnShowExternalBridgeLinkAction
  | OnToAddressChangeAction
  | OnToNetworkChangeAction
  | OnTokenAssetChangeAction
  | OnTokenBalanceChangeAction
  | OnUpdateValidationErrorsAction;

function getCronosEthereumTransferType(
  fromNetwork: Network,
  toNetwork: Network,
  value: CronosEthereumTransferType = CronosEthereumTransferType.CronosPool
): CronosEthereumTransferType | null {
  if (fromNetwork === Network.Cronos && toNetwork === Network.Ethereum) {
    // Whenever we switch to Cronos -> Ethereum, set the CronosEthereumTransferType to CronosPool by default
    return value;
  }

  return null;
}

function reducer(
  state: TransferAssetFlowReducerState,
  action: TransferAssetFlowReducerAction
): TransferAssetFlowReducerState {
  switch (action.type) {
    case ON_TO_ADDRESS_CHANGE:
      return { ...state, toAddress: action.value };
    case ON_CRONOS_ETHEREUM_TRANSFER_TYPE_CHANGE: {
      const newState: TransferAssetFlowReducerState = { ...state };

      newState.cronosEthereumTransferType = getCronosEthereumTransferType(
        newState.fromNetwork,
        newState.toNetwork,
        action.value
      );

      return newState;
    }
    case ON_CONFIRM_FEE_INFO:
      return { ...state, feeInfo: action.value };
    case ON_DEFAULT_NETWORKS_CHANGE:
      return {
        ...initState,
        defaultFromNetwork: action.value.fromNetwork,
        defaultToNetwork: action.value.toNetwork,
        fromNetwork: action.value.fromNetwork,
        toNetwork: action.value.toNetwork,
        toAddress: state.toAddress,
      };
    case ON_NEXT_STEP:
      if (state.step === TransferAssetFlowStep.ProvideDetails) {
        return {
          ...state,
          amount: sanitizeInput(state.amount, 6),
          showAssetSelection: false,
          step: TransferAssetFlowStep.ConfirmDetails,
        };
      }

      if (state.step === TransferAssetFlowStep.ConfirmDetails) {
        return { ...state, step: TransferAssetFlowStep.BridgeTransaction };
      }

      return { ...state };
    case ON_PREV_STEP:
      if (state.step === TransferAssetFlowStep.ConfirmDetails) {
        return {
          ...state,
          step: TransferAssetFlowStep.ProvideDetails,
        };
      }

      return { ...state };
    case ON_FROM_NETWORK_CHANGE: {
      const newState: TransferAssetFlowReducerState = {
        ...state,
        fromNetwork: action.value,
        hasSwitchedNetwork:
          state.fromNetwork === action.value ? state.hasSwitchedNetwork : true,
        tokenAsset: null,
        amount: '0',
      };

      newState.cronosEthereumTransferType = getCronosEthereumTransferType(
        newState.fromNetwork,
        newState.toNetwork
      );

      return newState;
    }
    case ON_HIDE_ASSET_SELECTION:
      return { ...state, showAssetSelection: false };
    case ON_HIDE_WALLET_CONNECTION:
      return { ...state, showWalletConnection: false };
    case ON_HIDE_EXTERNAL_BRIDGE_LINK:
      return { ...state, externalBridgeType: null };
    case ON_INITIAL_FROM_NETWORK_CHANGE: {
      const newState: TransferAssetFlowReducerState = {
        ...state,
        fromNetwork: action.value,
        tokenAsset: null,
        amount: '0',
      };

      newState.cronosEthereumTransferType = getCronosEthereumTransferType(
        newState.fromNetwork,
        newState.toNetwork
      );

      return newState;
    }
    case ON_INITIAL_TO_NETWORK_CHANGE: {
      const newState: TransferAssetFlowReducerState = {
        ...state,
        toNetwork: action.value,
        tokenAsset: null,
        amount: '0',
      };

      newState.cronosEthereumTransferType = getCronosEthereumTransferType(
        newState.fromNetwork,
        newState.toNetwork
      );

      return newState;
    }
    case ON_TO_NETWORK_CHANGE: {
      const newState: TransferAssetFlowReducerState = {
        ...state,
        toNetwork: action.value,
        tokenAsset: null,
        amount: '0',
        hasSwitchedNetwork:
          state.toNetwork === action.value ? state.hasSwitchedNetwork : true,
      };

      newState.cronosEthereumTransferType = getCronosEthereumTransferType(
        newState.fromNetwork,
        newState.toNetwork
      );

      return newState;
    }
    case ON_FLIP_NETWORKS: {
      const newState: TransferAssetFlowReducerState = {
        ...state,
        hasSwitchedNetwork: true,
        toNetwork: state.fromNetwork,
        fromNetwork: state.toNetwork,
        tokenAsset: null,
        amount: '0',
      };

      newState.cronosEthereumTransferType = getCronosEthereumTransferType(
        newState.fromNetwork,
        newState.toNetwork
      );

      return newState;
    }
    case ON_TOKEN_ASSET_CHANGE:
      return { ...state, tokenAsset: action.value };
    case ON_TOKEN_BALANCE_CHANGE:
      return { ...state, tokenBalance: action.value };
    case ON_AMOUNT_CHANGE: {
      const newState: TransferAssetFlowReducerState = {
        ...state,
        amount: restrictInput(action.value, 6),
      };

      if (action.clearAmountValidationErrors) {
        const newValidationErrors = { ...state.validationErrors };
        delete newValidationErrors['amount'];
        newState.validationErrors = newValidationErrors;
      }

      return newState;
    }
    case ON_RESET:
      return {
        ...initState,
        defaultFromNetwork: state.defaultFromNetwork,
        defaultToNetwork: state.defaultToNetwork,
        fromNetwork: state.defaultFromNetwork,
        toNetwork: state.defaultToNetwork,
        toAddress: state.toAddress,
      };
    case ON_SHOW_ASSET_SELECTION:
      return { ...state, showAssetSelection: true };
    case ON_SHOW_WALLET_CONNECTION:
      return { ...state, showWalletConnection: true };
    case ON_SHOW_EXTERNAL_BRIDGE_LINK:
      return { ...state, externalBridgeType: action.value };
    case ON_UPDATE_VALIDATION_ERRORS:
      return { ...state, validationErrors: action.value };
    default:
      return { ...state };
  }
}

export interface UseTransferAssetFlowStateResult {
  onAmountChange(
    v: string,
    options?: { clearAmountValidationErrors: boolean }
  ): void;
  onConfirmFeeInfo(v: UseBridgeTransactionFeeInfo): void;
  onCronosEthereumTransferTypeChange(v: CronosEthereumTransferType): void;
  onDefaultNetworksChange(v: OnDefaultNetworksChangeActionValue): void;
  onFlipNetworks(): void;
  onFromNetworkChange(v: Network): void;
  onHideAssetSelection(): void;
  onHideWalletConnection(): void;
  onHideExternalBridgeLink(): void;
  onInitialFromNetworkChange(v: Network): void;
  onInitialToNetworkChange(v: Network): void;
  onNextStep(): void;
  onPrevStep(): void;
  onReset(): void;
  onShowAssetSelection(): void;
  onShowWalletConnection(): void;
  onShowExternalBridgeLink(v: ExternalBridgeType): void;
  onToAddressChange(v: string): void;
  onToNetworkChange(v: Network): void;
  onTokenAssetChange(v: TokenAsset): void;
  onTokenBalanceChange(v: string): void;
  onValidate(params: ValidateParams): Promise<ValidateResult>;
  state: TransferAssetFlowReducerState;
}

function useTransferAssetFlowState(): UseTransferAssetFlowStateResult {
  const [state, dispatch] = useReducer(reducer, initState);
  const validate = useValidateTransferAssetFlow();

  async function onValidate(params: ValidateParams): Promise<ValidateResult> {
    const result = validate(params, state);
    const { ok, errors } = result;

    if (ok) {
      dispatch({ type: ON_UPDATE_VALIDATION_ERRORS, value: {} });
    } else {
      dispatch({ type: ON_UPDATE_VALIDATION_ERRORS, value: errors });
    }

    return result;
  }

  return {
    onToAddressChange: useCallback(
      (value: string): void => dispatch({ type: ON_TO_ADDRESS_CHANGE, value }),
      []
    ),
    onAmountChange: useCallback(
      (
        value: string,
        options?: { clearAmountValidationErrors: boolean }
      ): void =>
        dispatch({
          type: ON_AMOUNT_CHANGE,
          value,
          clearAmountValidationErrors:
            options?.clearAmountValidationErrors ?? false,
        }),
      []
    ),
    onConfirmFeeInfo: useCallback(
      (value: UseBridgeTransactionFeeInfo) =>
        dispatch({ type: ON_CONFIRM_FEE_INFO, value }),
      []
    ),
    onCronosEthereumTransferTypeChange: useCallback(
      (value: CronosEthereumTransferType) =>
        dispatch({ type: ON_CRONOS_ETHEREUM_TRANSFER_TYPE_CHANGE, value }),
      []
    ),
    onDefaultNetworksChange: useCallback(
      (value: OnDefaultNetworksChangeActionValue) =>
        dispatch({ type: ON_DEFAULT_NETWORKS_CHANGE, value }),
      []
    ),
    onFlipNetworks: useCallback(
      (): void => dispatch({ type: ON_FLIP_NETWORKS }),
      []
    ),
    onFromNetworkChange: useCallback(
      (value: Network): void =>
        dispatch({ type: ON_FROM_NETWORK_CHANGE, value }),
      []
    ),
    onHideAssetSelection: useCallback(
      (): void => dispatch({ type: ON_HIDE_ASSET_SELECTION }),
      []
    ),
    onHideWalletConnection: useCallback(
      (): void => dispatch({ type: ON_HIDE_WALLET_CONNECTION }),
      []
    ),
    onHideExternalBridgeLink: useCallback(
      (): void => dispatch({ type: ON_HIDE_EXTERNAL_BRIDGE_LINK }),
      []
    ),
    onInitialFromNetworkChange: useCallback(
      (value: Network): void =>
        dispatch({ type: ON_INITIAL_FROM_NETWORK_CHANGE, value }),
      []
    ),
    onInitialToNetworkChange: useCallback(
      (value: Network): void =>
        dispatch({ type: ON_INITIAL_TO_NETWORK_CHANGE, value }),
      []
    ),
    onNextStep: useCallback((): void => dispatch({ type: ON_NEXT_STEP }), []),
    onPrevStep: useCallback((): void => dispatch({ type: ON_PREV_STEP }), []),
    onReset: useCallback((): void => dispatch({ type: ON_RESET }), []),
    onShowAssetSelection: useCallback(
      (): void => dispatch({ type: ON_SHOW_ASSET_SELECTION }),
      []
    ),
    onShowWalletConnection: useCallback(
      (): void => dispatch({ type: ON_SHOW_WALLET_CONNECTION }),
      []
    ),
    onShowExternalBridgeLink: useCallback(
      (value: ExternalBridgeType): void =>
        dispatch({ type: ON_SHOW_EXTERNAL_BRIDGE_LINK, value }),
      []
    ),
    onToNetworkChange: useCallback(
      (value: Network): void => dispatch({ type: ON_TO_NETWORK_CHANGE, value }),
      []
    ),
    onTokenAssetChange: useCallback(
      (value: TokenAsset): void =>
        dispatch({ type: ON_TOKEN_ASSET_CHANGE, value }),
      []
    ),
    onTokenBalanceChange: useCallback(
      (value: string): void =>
        dispatch({ type: ON_TOKEN_BALANCE_CHANGE, value }),
      []
    ),
    onValidate,
    state,
  };
}

export default useTransferAssetFlowState;
